import { Star, ExternalLink, Zap } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { GOOGLE_REVIEW_URL, GOOGLE_RATING, GOOGLE_REVIEWS } from "@/lib/site";
import { scrollToSection } from "@/lib/scroll";

const Stars = ({ n = 5, size = 15, className = "text-neon" }) => (
  <span className={`flex gap-0.5 ${className}`} aria-label={`${n} étoiles sur 5`}>
    {Array.from({ length: n }).map((_, k) => (
      <Star key={k} size={size} fill="currentColor" strokeWidth={0} />
    ))}
  </span>
);

export default function Reviews() {
  return (
    <section id="avis" className="py-24 lg:py-32">
      <div className="mx-auto w-full max-w-7xl px-5 lg:px-8">
        <SectionHead
          tag="Avis clients"
          title={<>Note parfaite sur <span className="text-neon">Google</span></>}
          sub="Nos clients parlent de nous : note 5,0/5 sur notre fiche Google Business."
        />

        <div className="mt-14 grid gap-5 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal delay={0.08}>
            <div data-testid="google-rating-card" className="flex h-full flex-col items-start justify-between gap-8 rounded-3xl border border-neon/25 bg-gradient-to-b from-neon/10 via-ink to-ink p-8 text-center lg:items-center lg:p-10">
              <div className="flex flex-col items-center">
                <p data-testid="google-rating-score" className="font-display text-7xl font-black leading-none text-white">
                  {GOOGLE_RATING.score}
                </p>
                <div className="mt-3">
                  <Stars size={22} />
                </div>
                <p className="mt-3 text-sm text-slate-400">
                  Basé sur {GOOGLE_RATING.count} avis Google vérifiés
                </p>
                <a
                  data-testid="reviews-see-google"
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 flex items-center gap-2 text-xs font-bold text-neon hover:underline"
                >
                  Voir les avis sur Google
                  <ExternalLink size={13} />
                </a>
              </div>
              <button
                data-testid="reviews-google-btn"
                onClick={() => window.open(GOOGLE_REVIEW_URL, "_blank", "noopener")}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-neon px-6 py-4 text-xs font-extrabold uppercase tracking-wider text-night chip-glow transition-transform duration-200 hover:scale-[1.03]"
              >
                <Star size={15} fill="currentColor" strokeWidth={0} />
                Laisser un avis sur Google
              </button>
              <button
                data-testid="reviews-devis-btn"
                onClick={() => scrollToSection("#devis")}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-line px-6 py-3.5 text-xs font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:border-neon hover:text-neon"
              >
                <Zap size={14} className="text-neon" />
                Pas encore client ? Devis gratuit
              </button>
            </div>
          </Reveal>

          <div className="grid gap-4">
            {GOOGLE_REVIEWS.map((r, i) => (
              <Reveal key={r.name} delay={0.12 + i * 0.08}>
                <figure data-testid={`review-card-${i + 1}`} className="flex h-full flex-col justify-between rounded-2xl border border-line bg-ink p-7 transition-colors duration-300 hover:border-neon/40">
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full border border-neon/30 bg-neon/10 font-display text-sm font-extrabold text-neon">
                          {r.name.charAt(0)}
                        </span>
                        <div>
                          <figcaption className="text-sm font-bold text-white">{r.name}</figcaption>
                          <p className="text-[11px] text-slate-500">{r.when}</p>
                        </div>
                      </div>
                      <span className="rounded-full border border-line bg-night px-3 py-1 font-mono text-[9px] uppercase tracking-wider text-slate-400">
                        Avis Google
                      </span>
                    </div>
                    <div className="mt-4">
                      <Stars n={r.stars} />
                    </div>
                    <blockquote className="mt-3 text-sm leading-relaxed text-slate-400">
                      « {r.text} »
                    </blockquote>
                  </div>
                </figure>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <button
                data-testid="reviews-all-link"
                onClick={() => window.open(GOOGLE_REVIEW_URL, "_blank", "noopener")}
                className="w-full rounded-2xl border border-dashed border-line bg-ink p-4 text-xs font-semibold text-slate-400 transition-colors duration-200 hover:border-neon/40 hover:text-neon"
              >
                Tous les avis sont publiés sur notre fiche Google — cliquez pour les lire
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}